import { StringHelper } from '../helpers/strings.helper';
import { BlueIconType } from '../../lean-icons/blue-icons/blue-icons.component';

export const PRIORITY = { urgent: 0, warning: 1 } as const;

export interface CountDown {
    days?: number;
    hours?: number;
    minutes?: number;
    seconds?: number;
}

export const getKycCorrectionRoute = (isBrm?: boolean) => {
    const bo = 'main-app/business-user/kyc-correction';
    const brm = 'main-app/aggregator/business-owners/kyc-correction';
    return isBrm ? brm : bo;
};

export const FROM_KYC_BANNER_KEY = 'fromKycBanner';

export const FIX_ROUTES: Record<KycCorrectionItemKey, string> = {
    NIN: '/main-app/business-user/nin',
    CAC: '/main-app/business-user/cac',
    DOB: 'main-app/profile/update-date-of-birth',
    ADDRESS: '/main-app/profile/update-residential-address',
    BVN: '/onboarding-v2/kyc',
    LIVENESS: 'main-app/business-user/kyc-correction/facial-validation',
    RESIDENTIAL_ADDRESS_REVALIDATION: `/main-app/business-user/address-revalidation?addressType=residential`,
    BUSINESS_ADDRESS_REVALIDATION: `/main-app/business-user/address-revalidation?addressType=business`,
    PHONE: '',
    NAME: '',
    NIN_BVN_DOB: '',
    LEVEL_UPGRADE: '',
    NATIONALITY: 'main-app/business-user/kyc-correction/nationality-validation',
    PEP: 'main-app/business-user/kyc-correction/pep-validation',
    SOURCE_OF_WEALTH: 'main-app/business-user/kyc-correction/source-of-wealth-validation',
    TIN: `onboarding-v2/kyc-lvl4?${FROM_KYC_BANNER_KEY}=true`,
    AFFILATES: `onboarding-v2/kyc-lvl4?${FROM_KYC_BANNER_KEY}=true`,
    SCUML: `onboarding-v2/kyc-lvl4?${FROM_KYC_BANNER_KEY}=true`,
} as const;

export const getBrmFixRoutes = (id: string | number): Record<KycCorrectionItemKey, string> => ({
    NIN: '/main-app/business-user/nin',
    CAC: '/main-app/business-user/cac',
    DOB: `main-app/aggregator/business-owners/${id}/update-date-of-birth`,
    ADDRESS: `main-app/aggregator/business-owners/${id}/update-residential-address`,
    BVN: '/onboarding-v2/kyc',
    LIVENESS: `main-app/aggregator/business-owners/kyc-correction/facial-validation/${id}`,
    RESIDENTIAL_ADDRESS_REVALIDATION: `/main-app/aggregator/business-owners/address-revalidation/${id}?addressType=residential`,
    BUSINESS_ADDRESS_REVALIDATION: `/main-app/aggregator/business-owners/address-revalidation/${id}?addressType=business`,
    PHONE: '',
    NAME: '',
    NIN_BVN_DOB: '',
    LEVEL_UPGRADE: '',
    NATIONALITY: 'main-app/aggregator/business-owners/kyc-correction/nationality-validation',
    PEP: 'main-app/aggregator/business-owners/kyc-correction/pep-validation',
    SOURCE_OF_WEALTH: 'main-app/aggregator/business-owners/kyc-correction/source-of-wealth-validation',
    TIN: `onboarding-v2/brm-kyc-lvl4?${FROM_KYC_BANNER_KEY}=true`,
    AFFILATES: `onboarding-v2/brm-kyc-lvl4?${FROM_KYC_BANNER_KEY}=true`,
    SCUML: `onboarding-v2/brm-kyc-lvl4?${FROM_KYC_BANNER_KEY}=true`,
});

export function formatTime(time: number) {
    if (StringHelper.isNullOrEmptyOrUndefined(time) || time < 0) {
        return '00';
    }
    const formattedTime = time < 10 ? `0${time}` : time;
    return formattedTime.toString();
}

export function getCountDown(endTime: number, currentTime: number) {
    const difference = endTime - currentTime;
    const MILLISECONDS_IN_MINUTE = 1000 * 60;
    const MILLISECONDS_IN_HOUR = 1000 * 60 * 60;
    const MILLISECONDS_IN_DAY = 1000 * 60 * 60 * 24;
    const days = Math.floor(difference / MILLISECONDS_IN_DAY);
    const hours = Math.floor((difference % MILLISECONDS_IN_DAY) / MILLISECONDS_IN_HOUR);
    const minutes = Math.floor((difference % MILLISECONDS_IN_HOUR) / MILLISECONDS_IN_MINUTE);
    return { days, hours, minutes };
}

export interface KycCorrections {
    dateOfBirthStatus: KycCorrectionStatus;
    addressStatus: KycCorrectionStatus;
    levelZeroStatus: KycCorrectionStatus;
    defaultBvnStatus: KycCorrectionStatus;
    cacStatus: KycCorrectionStatus;
    ninStatus: KycCorrectionStatus;
    residentialAddressVerificationStatus: KycCorrectionStatus;
    businessAddressVerificationStatus: KycCorrectionStatus;
    posTerminalStatus: KycCorrectionStatus;
    livenessStatus?: KycCorrectionStatus;
    phoneStatus?: KycCorrectionStatus;
    nameStatus?: KycCorrectionStatus;
    ninBvnDateOfBirthStatus?: KycCorrectionStatus;
    nationalityStatus: KycCorrectionStatus;
    sourceOfWealthStatus: KycCorrectionStatus;
    pepStatus: KycCorrectionStatus;
    hasInCompleteInformation: boolean;
    shouldUpgradeKycToLevelTwo: boolean;
    shouldUpgradeKycToLevelThree: boolean;
    addressRejectionReason: string;
    dateOfBirthRejectionReason: string;
    cacRejectionReason: string;
    ninRejectionReason: string;
    businessAddressVerificationRejectionReason: string;
    residentialAddressVerificationRejectionReason: string;
    tinStatus: KycCorrectionStatus;
    bvnNinAffiliateStatus: KycCorrectionStatus;
    scumlAndOperatingLicenseStatus: KycCorrectionStatus;
}

interface KycCorrectionDetails {
    status: KycCorrectionStatus;
    rejectionReason?: string;
    priority: number;
}

export interface KycCorrectionsV2 {
    dateOfBirth: KycCorrectionDetails;
    address: KycCorrectionDetails;
    levelZero: KycCorrectionDetails;
    defaultBvn: KycCorrectionDetails;
    cac: KycCorrectionDetails;
    nin: KycCorrectionDetails;
    residentialAddressVerification: KycCorrectionDetails;
    businessAddressVerification: KycCorrectionDetails;
    posTerminal: KycCorrectionDetails;
    liveness: KycCorrectionDetails;
    phone: KycCorrectionDetails;
    name: KycCorrectionDetails;
    ninBvnDateOfBirth: KycCorrectionDetails;
    nationality: KycCorrectionDetails;
    sourceOfWealth: KycCorrectionDetails;
    pep: KycCorrectionDetails;
    tin: KycCorrectionDetails;
    bvnNinAffiliate: KycCorrectionDetails;
    scumlAndOperatingLicense: KycCorrectionDetails;
    hasInCompleteInformation: boolean;
    shouldUpgradeKycToLevelTwo: boolean;
    shouldUpgradeKycToLevelThree: boolean;
}

export const KYC_CORRECTION_STATUS = {
    COMPLETED: 'COMPLETED',
    PENDING: 'PENDING',
    REJECTED: 'REJECTED',
    IN_PROGRESS: 'IN_PROGRESS',
    REJECTED_TERMINAL: 'REJECTED_TERMINAL',
} as const;

export type KycCorrectionStatus = typeof KYC_CORRECTION_STATUS[keyof typeof KYC_CORRECTION_STATUS];

export const kycUIStatusMap: Record<KycCorrectionStatus, string> = {
    COMPLETED: 'completed',
    PENDING: 'pending',
    REJECTED: 'failed',
    IN_PROGRESS: 'verifying',
    REJECTED_TERMINAL: 'failed',
};

export interface KycCorrectionItem {
    key: KycCorrectionItemKey;
    title: string;
    icon: BlueIconType;
    getStatus?: (correction: KycCorrections | KycCorrectionsV2) => KycCorrectionStatus;
    getRejectionReason?: (correction: KycCorrections | KycCorrectionsV2) => string;
    canViewAction?: (correction: KycCorrections | KycCorrectionsV2) => boolean;
    active?: boolean;
    isRecheckItem?: boolean;
    isKycLevel4Issues?: boolean;
    isUpgradeItem?: boolean;
    infoMessage?: string;
    getPriority?: (correction: KycCorrections | KycCorrectionsV2) => number;
}

export type KycCorrectionItemKey =
    | 'DOB'
    | 'BVN'
    | 'NIN'
    | 'CAC'
    | 'ADDRESS'
    | 'LIVENESS'
    | 'RESIDENTIAL_ADDRESS_REVALIDATION'
    | 'BUSINESS_ADDRESS_REVALIDATION'
    | 'PHONE'
    | 'NAME'
    | 'NIN_BVN_DOB'
    | 'NATIONALITY'
    | 'PEP'
    | 'SOURCE_OF_WEALTH'
    | 'TIN'
    | 'AFFILATES'
    | 'SCUML'
    | 'LEVEL_UPGRADE';

export const kycCorrectionItems: KycCorrectionItem[] = [
    {
        key: 'DOB',
        title: 'Date of Birth',
        icon: 'blue-plain-avatar',
        getStatus: (correction: KycCorrections) => correction.dateOfBirthStatus,
        canViewAction: (correction: KycCorrections) => isFailedOrPending(correction.dateOfBirthStatus),
        getRejectionReason: (correction: KycCorrections) => correction.dateOfBirthRejectionReason,
        active: true,
        infoMessage: 'Provide the details of your date of birth',
    },
    {
        key: 'ADDRESS',
        title: 'Business Address',
        icon: 'blue-location-pin',
        getStatus: (correction: KycCorrections) => correction.addressStatus,
        canViewAction: (correction: KycCorrections) => isFailedOrPending(correction.addressStatus),
        getRejectionReason: (correction: KycCorrections) => correction.addressRejectionReason,
        active: true,
        infoMessage: 'Provide the details of your business location',
    },
    {
        key: 'BVN',
        title: 'Provide Your BVN',
        icon: 'blue-id-card',
        getStatus: (correction: KycCorrections) => getBvnStatus(correction),
        canViewAction: (correction: KycCorrections) => isFailedOrPending(getBvnStatus(correction)),
        active: true,
        infoMessage: 'Your Bank Verification Number is required',
    },
    {
        key: 'NIN',
        title: 'Provide your NIN',
        icon: 'blue-id-card',
        getStatus: (correction: KycCorrections) => correction.ninStatus,
        canViewAction: (correction: KycCorrections) => isFailedOrPending(correction.ninStatus),
        getRejectionReason: (correction: KycCorrections) => correction.ninRejectionReason,
        active: true,
        infoMessage: 'Your National Identification Number is required',
    },
    {
        key: 'CAC',
        title: 'Provide your CAC',
        icon: 'blue-plain-certificate',
        getStatus: (correction: KycCorrections) => correction.cacStatus,
        canViewAction: (correction: KycCorrections) => canViewCac(correction.cacStatus),
        getRejectionReason: (correction: KycCorrections) => correction.cacRejectionReason,
        active: true,
        infoMessage: 'Provide your business CAC documents',
    },
    {
        key: 'LIVENESS',
        title: 'Liveness check and facial validation',
        icon: 'blue-plain-avatar',
        getStatus: (correction: KycCorrections) => correction?.livenessStatus,
        canViewAction: (correction: KycCorrections) => isFailedOrPending(correction?.livenessStatus),
        active: true,
        infoMessage: 'Your face needs to be verified against your BVN information',
    },
    {
        key: 'RESIDENTIAL_ADDRESS_REVALIDATION',
        title: 'Update Residential Address',
        icon: 'blue-download',
        getStatus: (correction: KycCorrections) => correction.residentialAddressVerificationStatus,
        canViewAction: (correction: KycCorrections) => isFailedOrPending(correction.residentialAddressVerificationStatus),
        getRejectionReason: (correction: KycCorrections) => correction.residentialAddressVerificationRejectionReason,
        infoMessage: 'Provide the details of where you live',
        active: true,
    },
    {
        key: 'BUSINESS_ADDRESS_REVALIDATION',
        title: 'Update Business Address',
        icon: 'blue-download',
        getStatus: (correction: KycCorrections) => correction.businessAddressVerificationStatus,
        canViewAction: (correction: KycCorrections) => isFailedOrPending(correction.businessAddressVerificationStatus),
        getRejectionReason: (correction: KycCorrections) => correction.businessAddressVerificationRejectionReason,
        infoMessage: 'Provide the details of your business location',
        active: true,
    },
    {
        key: 'PHONE',
        title: 'Phone Number',
        icon: 'blue-phone-sm',
        getStatus: (correction: KycCorrections) => correction?.phoneStatus,
        canViewAction: (correction: KycCorrections) => isFailedOrPending(correction.phoneStatus),
        active: true,
        isRecheckItem: true,
        infoMessage:
            'The phone numbers on your BVN and NIN do not match. To fix this issue visit your bank branch or nearest NIMC enrolment centre to update your details',
    },
    {
        key: 'NAME',
        title: 'Full Name',
        icon: 'blue-id-card',
        getStatus: (correction: KycCorrections) => correction?.nameStatus,
        canViewAction: (correction: KycCorrections) => isFailedOrPending(correction.nameStatus),
        active: true,
        isRecheckItem: true,
        infoMessage:
            'The name on your BVN and NIN do not match. To fix this issue visit your bank branch or nearest NIMC enrolment centre to update your details',
    },
    {
        key: 'NIN_BVN_DOB',
        title: 'Date of Birth',
        icon: 'blue-plain-avatar',
        getStatus: (correction: KycCorrections) => correction?.ninBvnDateOfBirthStatus,
        canViewAction: (correction: KycCorrections) => isFailedOrPending(correction.ninBvnDateOfBirthStatus),
        active: true,
        isRecheckItem: true,
        infoMessage:
            'The date of birth on your BVN and NIN do not match. To fix this issue visit your bank branch or nearest NIMC enrolment centre to update your details',
    },
    {
        key: 'LEVEL_UPGRADE',
        title: 'Upgrade to Level 3',
        icon: 'upload',
        getStatus: (correction: KycCorrections) =>
            correction?.shouldUpgradeKycToLevelTwo || correction?.shouldUpgradeKycToLevelThree
                ? KYC_CORRECTION_STATUS.PENDING
                : KYC_CORRECTION_STATUS.COMPLETED,
        canViewAction: (correction: KycCorrections) => correction?.shouldUpgradeKycToLevelTwo || correction?.shouldUpgradeKycToLevelThree,
        active: true,
        isUpgradeItem: true,
        infoMessage: 'Upgrade your KYC level to continue operating your POS terminals',
    },
    {
        key: 'NATIONALITY',
        title: 'Nationality',
        icon: 'world',
        getStatus: (correction: KycCorrections) => correction?.nationalityStatus,
        canViewAction: (correction: KycCorrections) => isFailedOrPending(correction.nationalityStatus),
        active: true,
        infoMessage: 'Provide your nationality',
    },
    {
        key: 'SOURCE_OF_WEALTH',
        title: 'Source of Revenue',
        icon: 'standalone-briefcase-sm',
        getStatus: (correction: KycCorrections) => correction?.sourceOfWealthStatus,
        canViewAction: (correction: KycCorrections) => isFailedOrPending(correction.sourceOfWealthStatus),
        active: true,
        infoMessage: 'Provide the details of your source of revenue',
    },
    {
        key: 'PEP',
        title: 'PEP Status',
        icon: 'users',
        getStatus: (correction: KycCorrections) => correction?.pepStatus,
        canViewAction: (correction: KycCorrections) => isFailedOrPending(correction.pepStatus),
        active: true,
        infoMessage: 'Provide details of your political exposure',
    },
    {
        key: 'TIN',
        icon: 'blue-id-card',
        title: 'Tax Identification Number',
        getStatus: (correction: KycCorrections) => correction?.tinStatus,
        canViewAction: (correction: KycCorrections) => isFailedOrPending(correction.tinStatus),
        active: true,
        isKycLevel4Issues: true,
        infoMessage: 'Provide your tax identification number',
    },
    {
        key: 'AFFILATES',
        icon: 'users',
        title: 'Business Affilates',
        getStatus: (correction: KycCorrections) => correction?.bvnNinAffiliateStatus,
        canViewAction: (correction: KycCorrections) => isFailedOrPending(correction.bvnNinAffiliateStatus),
        active: true,
        isKycLevel4Issues: true,
        infoMessage: 'Provide details of your business affiliates',
    },

    {
        key: 'SCUML',
        icon: 'blue-plain-certificate',
        title: 'SCUML Details',
        getStatus: (correction: KycCorrections) => correction?.scumlAndOperatingLicenseStatus,
        canViewAction: (correction: KycCorrections) => isFailedOrPending(correction.scumlAndOperatingLicenseStatus),
        active: true,
        isKycLevel4Issues: true,
        infoMessage: 'Provide details of your SCUML and business operating license',
    },
];

export const kycCorrectionItemsV2: KycCorrectionItem[] = [
    {
        key: 'DOB',
        title: 'Date of Birth',
        icon: 'blue-plain-avatar',
        getStatus: (correction: KycCorrectionsV2) => correction?.dateOfBirth.status,
        canViewAction: (correction: KycCorrectionsV2) => isFailedOrPending(correction?.dateOfBirth.status),
        getRejectionReason: (correction: KycCorrectionsV2) => correction?.dateOfBirth.rejectionReason,
        active: true,
        infoMessage: 'Provide the details of your date of birth',
        getPriority: (correction: KycCorrectionsV2) => correction?.dateOfBirth.priority,
    },
    {
        key: 'ADDRESS',
        title: 'Business Address',
        icon: 'blue-location-pin',
        getStatus: (correction: KycCorrectionsV2) => correction?.address.status,
        canViewAction: (correction: KycCorrectionsV2) => isFailedOrPending(correction?.address.status),
        getRejectionReason: (correction: KycCorrectionsV2) => correction?.address.rejectionReason,
        active: true,
        infoMessage: 'Provide the details of your business location',
        getPriority: (correction: KycCorrectionsV2) => correction?.address.priority,
    },
    {
        key: 'BVN',
        title: 'Provide Your BVN',
        icon: 'blue-id-card',
        getStatus: (correction: KycCorrectionsV2) =>
            getBvnStatus({
                defaultBvnStatus: correction?.defaultBvn.status,
                levelZeroStatus: correction?.levelZero.status,
            }),
        canViewAction: (correction: KycCorrectionsV2) =>
            isFailedOrPending(
                getBvnStatus({
                    defaultBvnStatus: correction?.defaultBvn.status,
                    levelZeroStatus: correction?.levelZero.status,
                }),
            ),
        active: true,
        infoMessage: 'Your Bank Verification Number is required',
        getPriority: (correction: KycCorrectionsV2) =>
            Math.min(correction?.defaultBvn.priority ?? Infinity, correction?.levelZero.priority ?? Infinity),
    },
    {
        key: 'NIN',
        title: 'Provide your NIN',
        icon: 'blue-id-card',
        getStatus: (correction: KycCorrectionsV2) => correction?.nin.status,
        canViewAction: (correction: KycCorrectionsV2) => isFailedOrPending(correction?.nin.status),
        getRejectionReason: (correction: KycCorrectionsV2) => correction?.nin.rejectionReason,
        active: true,
        infoMessage: 'Your National Identification Number is required',
        getPriority: (correction: KycCorrectionsV2) => correction?.nin.priority,
    },
    {
        key: 'CAC',
        title: 'Provide your CAC',
        icon: 'blue-plain-certificate',
        getStatus: (correction: KycCorrectionsV2) => correction?.cac.status,
        canViewAction: (correction: KycCorrectionsV2) => canViewCac(correction?.cac.status),
        getRejectionReason: (correction: KycCorrectionsV2) => correction?.cac.rejectionReason,
        active: true,
        infoMessage: 'Provide your business CAC documents',
        getPriority: (correction: KycCorrectionsV2) => correction?.cac.priority,
    },
    {
        key: 'LIVENESS',
        title: 'Liveness check and facial validation',
        icon: 'blue-plain-avatar',
        getStatus: (correction: KycCorrectionsV2) => correction?.liveness.status,
        canViewAction: (correction: KycCorrectionsV2) => isFailedOrPending(correction?.liveness.status),
        active: true,
        getPriority: (correction: KycCorrectionsV2) => correction?.liveness.priority,
        infoMessage: 'Your face needs to be verified against your BVN information',
    },
    {
        key: 'RESIDENTIAL_ADDRESS_REVALIDATION',
        title: 'Update Residential Address',
        icon: 'blue-location-pin',
        getStatus: (correction: KycCorrectionsV2) => correction?.residentialAddressVerification.status,
        canViewAction: (correction: KycCorrectionsV2) => isFailedOrPending(correction?.residentialAddressVerification.status),
        getRejectionReason: (correction: KycCorrectionsV2) => correction?.residentialAddressVerification.rejectionReason,
        active: true,
        infoMessage: 'Provide the details of where you live',
        getPriority: (correction: KycCorrectionsV2) => correction?.residentialAddressVerification.priority,
    },
    {
        key: 'BUSINESS_ADDRESS_REVALIDATION',
        title: 'Update Business Address',
        icon: 'blue-location-pin',
        getStatus: (correction: KycCorrectionsV2) => correction?.businessAddressVerification.status,
        canViewAction: (correction: KycCorrectionsV2) => isFailedOrPending(correction?.businessAddressVerification.status),
        getRejectionReason: (correction: KycCorrectionsV2) => correction?.businessAddressVerification.rejectionReason,
        active: true,
        infoMessage: 'Provide the details of your business location',
        getPriority: (correction: KycCorrectionsV2) => correction?.businessAddressVerification.priority,
    },
    {
        key: 'PHONE',
        title: 'Phone Number',
        icon: 'blue-phone-sm',
        getStatus: (correction: KycCorrectionsV2) => correction?.phone.status,
        canViewAction: (correction: KycCorrectionsV2) => isFailedOrPending(correction?.phone.status),
        active: true,
        isRecheckItem: true,
        infoMessage:
            'The phone numbers on your BVN and NIN do not match. To fix this issue visit your bank branch or nearest NIMC enrolment centre to update your details',
        getPriority: (correction: KycCorrectionsV2) => correction?.phone.priority,
    },
    {
        key: 'NAME',
        title: 'Full Name',
        icon: 'blue-id-card',
        getStatus: (correction: KycCorrectionsV2) => correction?.name.status,
        canViewAction: (correction: KycCorrectionsV2) => isFailedOrPending(correction?.name.status),
        active: true,
        isRecheckItem: true,
        infoMessage:
            'The name on your BVN and NIN do not match. To fix this issue visit your bank branch or nearest NIMC enrolment centre to update your details',
        getPriority: (correction: KycCorrectionsV2) => correction?.name.priority,
    },
    {
        key: 'NIN_BVN_DOB',
        title: 'Date of Birth',
        icon: 'blue-plain-avatar',
        getStatus: (correction: KycCorrectionsV2) => correction?.ninBvnDateOfBirth.status,
        canViewAction: (correction: KycCorrectionsV2) => isFailedOrPending(correction?.ninBvnDateOfBirth.status),
        active: true,
        isRecheckItem: true,
        infoMessage:
            'The date of birth on your BVN and NIN do not match. To fix this issue visit your bank branch or nearest NIMC enrolment centre to update your details',
        getPriority: (correction: KycCorrectionsV2) => correction?.ninBvnDateOfBirth.priority,
    },
    {
        key: 'LEVEL_UPGRADE',
        title: 'Upgrade to Level 3',
        icon: 'upload',
        getStatus: (correction: KycCorrectionsV2) =>
            correction?.shouldUpgradeKycToLevelTwo || correction?.shouldUpgradeKycToLevelThree
                ? KYC_CORRECTION_STATUS.PENDING
                : KYC_CORRECTION_STATUS.COMPLETED,
        canViewAction: (correction: KycCorrectionsV2) => correction?.shouldUpgradeKycToLevelTwo || correction?.shouldUpgradeKycToLevelThree,
        active: true,
        infoMessage: 'Upgrade your KYC level to continue operating your POS terminals',
        isUpgradeItem: true,
        getPriority: () => PRIORITY.urgent,
    },
    {
        key: 'NATIONALITY',
        title: 'Nationality',
        icon: 'world',
        getStatus: (correction: KycCorrectionsV2) => correction?.nationality.status,
        canViewAction: (correction: KycCorrectionsV2) => isFailedOrPending(correction?.nationality.status),
        active: true,
        getPriority: (correction: KycCorrectionsV2) => correction?.nationality.priority,
        infoMessage: 'Provide your nationality',
    },
    {
        key: 'SOURCE_OF_WEALTH',
        title: 'Source of Revenue',
        icon: 'standalone-briefcase-sm',
        getStatus: (correction: KycCorrectionsV2) => correction?.sourceOfWealth.status,
        canViewAction: (correction: KycCorrectionsV2) => isFailedOrPending(correction?.sourceOfWealth.status),
        active: true,
        infoMessage: 'Provide the details of your source of revenue',
        getPriority: (correction: KycCorrectionsV2) => correction?.sourceOfWealth.priority,
    },
    {
        key: 'PEP',
        title: 'PEP Status',
        icon: 'users',
        getStatus: (correction: KycCorrectionsV2) => correction?.pep.status,
        canViewAction: (correction: KycCorrectionsV2) => isFailedOrPending(correction?.pep.status),
        active: true,
        infoMessage: 'Provide details of your political exposure',
        getPriority: (correction: KycCorrectionsV2) => correction?.pep.priority,
    },
    {
        key: 'TIN',
        icon: 'blue-id-card',
        title: 'Tax Identification Number',
        getStatus: (correction: KycCorrectionsV2) => correction?.tin.status,
        canViewAction: (correction: KycCorrectionsV2) => isFailedOrPending(correction.tin.status),
        active: true,
        isKycLevel4Issues: true,
        getPriority: (correction: KycCorrectionsV2) => correction?.tin.priority,
        infoMessage: 'Provide your tax identification number',
    },
    {
        key: 'AFFILATES',
        icon: 'users',
        title: 'Business Affilates',
        getStatus: (correction: KycCorrectionsV2) => correction?.bvnNinAffiliate.status,
        canViewAction: (correction: KycCorrectionsV2) => isFailedOrPending(correction.bvnNinAffiliate.status),
        active: true,
        isKycLevel4Issues: true,
        getPriority: (correction: KycCorrectionsV2) => correction?.bvnNinAffiliate.priority,
        infoMessage: 'Provide details of your business affiliates',
    },

    {
        key: 'SCUML',
        icon: 'blue-plain-certificate',
        title: 'SCUML Details',
        getStatus: (correction: KycCorrectionsV2) => correction?.scumlAndOperatingLicense.status,
        canViewAction: (correction: KycCorrectionsV2) => isFailedOrPending(correction.scumlAndOperatingLicense.status),
        active: true,
        isKycLevel4Issues: true,
        getPriority: (correction: KycCorrectionsV2) => correction?.scumlAndOperatingLicense.priority,
        infoMessage: 'Provide details of your SCUML and business operating license',
    },
];

const getBvnStatus = (correction: { defaultBvnStatus: string; levelZeroStatus: string }) => {
    const { defaultBvnStatus, levelZeroStatus } = correction;
    if (defaultBvnStatus === 'COMPLETED' && levelZeroStatus === 'COMPLETED') {
        return 'COMPLETED';
    }
    if (defaultBvnStatus === 'PENDING' || levelZeroStatus === 'PENDING') {
        return 'PENDING';
    }
    if (defaultBvnStatus === 'IN_PROGRESS' || levelZeroStatus === 'IN_PROGRESS') {
        return 'IN_PROGRESS';
    }
    return 'REJECTED';
};

const includesStatus = (statuses: KycCorrectionStatus[], status: KycCorrectionStatus) => {
    return statuses.includes(status);
};

const isFailedOrPending = (status: KycCorrectionStatus) => {
    return includesStatus(['PENDING', 'REJECTED'], status);
};

const canViewCac = (status: KycCorrectionStatus) => {
    return includesStatus(['PENDING', 'REJECTED', 'IN_PROGRESS'], status);
};

export type KycBannerMode = 'BO' | 'BRM';

export const RECHECK_STATUS_JOB_TYPES: Partial<Record<KycCorrectionItemKey, string>> = {
    PHONE: 'PHONE_STATUS',
    NAME: 'NAME_STATUS',
    NIN_BVN_DOB: 'DATE_OF_BIRTH_STATUS',
};

export enum KYC_LEVEL4_ISSUES {
    'TIN' = 'TIN',
    'AFFILATES' = 'AFFILATES',
    'SCUML' = 'SCUML',
}

export const BrmBusinessOwnerListCompilanceList = '/main-app/aggregator/business-owners/list?currentTab=noncompliant';
